import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet";
import "./Minecraft.css";
import "./public.css";
import 'bootstrap/dist/css/bootstrap.css';
import Flag from 'react-world-flags';
//https://en.wikipedia.org/wiki/ISO_3166-1_numeric

const minecraftPackageIcons = {
  coal: "https://raw.githubusercontent.com/PrismarineJS/minecraft-assets/master/data/1.20.2/items/coal.png",
  iron: "https://raw.githubusercontent.com/PrismarineJS/minecraft-assets/master/data/1.20.2/items/iron_ingot.png",
  gold: "https://raw.githubusercontent.com/PrismarineJS/minecraft-assets/master/data/1.20.2/items/gold_ingot.png",
  diamond: "https://raw.githubusercontent.com/PrismarineJS/minecraft-assets/master/data/1.20.2/items/diamond.png",
  emerald: "https://raw.githubusercontent.com/PrismarineJS/minecraft-assets/master/data/1.20.2/items/emerald.png",
  netherite: "https://raw.githubusercontent.com/PrismarineJS/minecraft-assets/master/data/1.20.2/items/netherite_ingot.png",
  oak: "https://raw.githubusercontent.com/InventivetalentDev/minecraft-assets/1.20.2/assets/minecraft/textures/block/oak_log.png",
  birch: "https://raw.githubusercontent.com/InventivetalentDev/minecraft-assets/1.20.2/assets/minecraft/textures/block/birch_log.png",
  spruce: "https://raw.githubusercontent.com/InventivetalentDev/minecraft-assets/1.20.2/assets/minecraft/textures/block/spruce_log.png",
  jungle: "https://raw.githubusercontent.com/InventivetalentDev/minecraft-assets/1.20.2/assets/minecraft/textures/block/jungle_log.png",
  acacia: "https://raw.githubusercontent.com/InventivetalentDev/minecraft-assets/1.20.2/assets/minecraft/textures/block/acacia_log.png",
  cherry: "https://raw.githubusercontent.com/InventivetalentDev/minecraft-assets/1.20.2/assets/minecraft/textures/block/cherry_log.png",
};

const getPackageIcon = (name = "") => {
  const key = name.toLowerCase();
  return minecraftPackageIcons[key] || "/img/Minecraft.png";
};


const latencyProbeByLocation = {
  "us east": "https://sky.sodiumhosting.com:8080",
  "atlanta": "https://sky.sodiumhosting.com:8080",
  "germany": "https://aston-game-ger.sodiumhosting.com:8080",
  "frankfurt": "https://aston-game-ger.sodiumhosting.com:8080",
  "uk": "https://lon-gb-ping.vultr.com",
  "united kingdom": "https://lon-gb-ping.vultr.com",
  "japan": "https://hnd-jp-ping.vultr.com",
  "australia": "https://syd-au-ping.vultr.com",
};

const getProbeUrlFromLocationName = (name = "") => {
  const normalized = name.trim().toLowerCase();
  return latencyProbeByLocation[normalized] || null;
};

const measureLatency = (url, timeoutMs = 1200) => new Promise((resolve) => {
  if (!url) {
    resolve(null);
    return;
  }

  const img = new Image();
  const started = performance.now();
  const timer = setTimeout(() => {
    img.src = "";
    resolve(null);
  }, timeoutMs);

  const finish = () => {
    clearTimeout(timer);
    resolve(Math.round(performance.now() - started));
  };

  img.onload = finish;
  img.onerror = finish;
  img.src = `${url}/favicon.ico?ts=${Date.now()}-${Math.random()}`;
});

export const Minecraft = () => {
  const [packageDetails, setPackageDetails] = useState([]);
  const [locationOption, setlocationOption] = useState([]);
  const [locations, setlocations] = useState([]);
  const [affiliateID, setAffiliateID] = useState(null);
  const [activeLocation, setActiveLocation] = useState(null);
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [recommendedPlayers, setRecommendedPlayers] = useState(5);
  const [recommendedMods, setRecommendedMods] = useState(0);
  const [recommendedPlugins, setRecommendedPlugins] = useState(0);
  const [latencyByLocation, setLatencyByLocation] = useState({});
  const [isPinging, setIsPinging] = useState(false);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const pageUrl = `${origin}/minecraft`;
  const description = "Minecraft hosting with instant setup, global locations, and a control panel made for mods and communities.";

  useEffect(() => {
    fetch("/minecraft.json")
      .then(res => res.json())
      .then(data => {
        setPackageDetails(data.packages);
        setlocationOption(data.locationOption);
        setlocations(data.locations);

        if (data.locations && data.locations.length > 0) {
          setActiveLocation(data.locations[0].id);
        }
      })
      .catch(error => console.error(error));

    const getCookie = (name) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
    };

    const affiliateIDFromCookie = getCookie("partnerAffiliateID");
    if (affiliateIDFromCookie) {
      setAffiliateID(affiliateIDFromCookie);
    }
  }, []);


  const handleLocationSelect = (locationId) => {
    setActiveLocation(locationId);
  };

  const estimatedRamGb = useMemo(() => {
    const baseRamGb = 3;
    const playerWeight = recommendedPlayers * 0.22;
    const modWeight = recommendedMods * 0.045;
    const pluginWeight = recommendedPlugins * 0.03;
    const computed = baseRamGb + playerWeight + modWeight + pluginWeight;
    return Number(Math.min(24, Math.max(3, computed)).toFixed(1));
  }, [recommendedMods, recommendedPlayers, recommendedPlugins]);

  const sortedPackages = useMemo(() => (
    [...packageDetails].sort((a, b) => a.memory - b.memory)
  ), [packageDetails]);

  const recommendedPackage = useMemo(() => {
    if (sortedPackages.length === 0) {
      return null;
    }

    return sortedPackages.find((pack) => (pack.memory / 1024) >= estimatedRamGb) || sortedPackages[sortedPackages.length - 1];
  }, [estimatedRamGb, sortedPackages]);

  const formatSliderValue = (value, maxValue) => (value >= maxValue ? `${maxValue}+` : value);

  const formatPingLabel = (locationId) => (
    typeof latencyByLocation[locationId] === "number"
      ? `Ping ${latencyByLocation[locationId]}ms`
      : "Ping unavailable"
  );

  const sortedLocations = useMemo(() => {
    return [...locations].sort((a, b) => {
      const aLatency = latencyByLocation[a.id];
      const bLatency = latencyByLocation[b.id];

      if (typeof aLatency !== "number" && typeof bLatency !== "number") {
        return 0;
      }
      if (typeof aLatency !== "number") {
        return 1;
      }
      if (typeof bLatency !== "number") {
        return -1;
      }
      return aLatency - bLatency;
    });
  }, [latencyByLocation, locations]);


  const runLatencyCheck = useCallback(async () => {
    if (locations.length === 0) {
      return;
    }

    setIsPinging(true);
    const nextLatencies = {};

    await Promise.all(locations.map(async (location) => {
      const probeUrl = getProbeUrlFromLocationName(location.name);
      const measured = await measureLatency(probeUrl);
      nextLatencies[location.id] = typeof measured === "number" && measured < 1200 ? measured : null;
    }));

    setLatencyByLocation(nextLatencies);
    setIsPinging(false);
  }, [locations]);

  useEffect(() => {
    runLatencyCheck();
  }, [runLatencyCheck]);

  const openSplitModal = () => setShowSplitModal(true);
  const closeSplitModal = () => setShowSplitModal(false);
  const openMigrationModal = () => setShowMigrationModal(true);
  const closeMigrationModal = () => setShowMigrationModal(false);

  return (
    <div className="game-page minecraft-page">
      <Helmet>
        <title>FlintHost | Minecraft Hosting</title>
        <meta name="description" content={description} />
        <meta property="og:title" content="FlintHost | Minecraft Hosting" />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={`${origin}/img/Logo2.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="FlintHost | Minecraft Hosting" />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={`${origin}/img/Logo2.png`} />
      </Helmet>
      <section className="game-hero">
        <div className="game-hero-content">
          <p className="game-eyebrow">Minecraft Java Hosting</p>
          <h1>Launch your Minecraft server today.</h1>
          <p className="game-lead">
            From lightweight SMP servers to heavily modded adventures, deploy fast and keep gameplay smooth with low-latency regions.
          </p>
          <div className="game-pill-row">
            <span>Instant setup</span>
            <span>Mod-ready</span>
            <span>Always-on monitoring</span>
          </div>
        </div>
        <div className="location-actions">
          <button type="button" className="ghost-button reping-button" onClick={runLatencyCheck} disabled={isPinging}>
            {isPinging ? "Re-pinging..." : "Re-ping locations"}
          </button>
        </div>
      </section>

      <section className="game-section">
        <div className="section-heading">
          <h2 className="section-title">Choose your region</h2>
          <p className="section-subtitle">Pick the region that gives your players the best connection.</p>
        </div>
        <div className="location-panel">
          <div className="location-grid">
            {sortedLocations.map(location => (
              <button
                key={location.id}
                className={`locationbox ${activeLocation === location.id ? 'is-active' : ''}`}
                onClick={() => handleLocationSelect(location.id)}
              >
                <span className="location-title">
                  <Flag code={location.countryCode} style={{ width: "20px", marginRight: "10px" }} />
                  {location.name}
                </span>
                <div className="location-meta">
                  <span className="location-action">
                    {activeLocation === location.id ? 'Selected' : 'Select'}
                  </span>
                  <span className="location-ping">
                    {formatPingLabel(location.id)}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="game-section">
        <div className="section-heading">
          <h2 className="section-title">Plan recommender</h2>
          <p className="section-subtitle">Set your expected players, mods, and plugins to get a suggested plan.</p>
        </div>
        <div className="recommendator-card">
          <div className="recommendator-sliders">
            <label htmlFor="players-range">Players <span>{formatSliderValue(recommendedPlayers, 20)}</span></label>
            <p className="recommendator-hint">Higher player counts need more RAM headroom.</p>
            <input
              id="players-range"
              type="range"
              min="0"
              max="20"
              value={recommendedPlayers}
              onChange={(event) => setRecommendedPlayers(Number(event.target.value))}
            />

            <label htmlFor="plugins-range">Plugins <span>{formatSliderValue(recommendedPlugins, 100)}</span></label>
            <p className="recommendator-hint">Plugins can add steady background load.</p>
            <input
              id="plugins-range"
              type="range"
              min="0"
              max="100"
              value={recommendedPlugins}
              onChange={(event) => setRecommendedPlugins(Number(event.target.value))}
            />

            <label htmlFor="mods-range">Mods <span>{formatSliderValue(recommendedMods, 300)}</span></label>
            <p className="recommendator-hint">Heavy modpacks scale RAM usage quickly.</p>
            <input
              id="mods-range"
              type="range"
              min="0"
              max="300"
              value={recommendedMods}
              onChange={(event) => setRecommendedMods(Number(event.target.value))}
            />
          </div>

          {recommendedPackage && (
            <div className="recommendator-result">
              <p className="recommendator-eyebrow">Recommended</p>
              <div className="recommendator-package">
                <img
                  className="packagebox-icon"
                  src={getPackageIcon(recommendedPackage.name)}
                  alt={`${recommendedPackage.name} tier icon`}
                  loading="lazy"
                />
                <div>
                  <h3>{recommendedPackage.name}</h3>
                  <p>{recommendedPackage.memory / 1024} GB RAM • {recommendedPackage.cpu / 100} vCores</p>
                  <p>Estimated RAM target: {estimatedRamGb} GB</p>
                  <p>For the best experience, we recommend this package for your current setup.</p>
                </div>
              </div>
              <a
                className="primary-link"
                href={`${recommendedPackage.link}?options[${locationOption}]=${activeLocation}${affiliateID ? `&ref=${encodeURIComponent(affiliateID)}` : ""}`}
              >
                Choose {recommendedPackage.name}
              </a>
            </div>
          )}
        </div>
      </section>

      <section className="game-section">
        <div className="section-heading">
          <h2 className="section-title">Minecraft Server Packages</h2>
          <p className="section-subtitle">Pick the memory and CPU bundle that matches your player count.</p>
        </div>
        <div className="game-cont">
          <div className="pricing-grid">
            {packageDetails.map(packageDetail => (
              <div key={packageDetail.id} className={`packagebox ${packageDetail.id}`}>
                <div className="packagebox-header">
                  <img
                    className="packagebox-icon"
                    src={getPackageIcon(packageDetail.name)}
                    alt={`${packageDetail.name} tier icon`}
                    loading="lazy"
                  />
                  <h3>{packageDetail.name}</h3>
                </div>
                <p>{packageDetail.memory / 1024} GB RAM</p>
                <p>{packageDetail.cpu / 100} {packageDetail.cpu / 100 === 1 ? "vCore" : "vCores"}</p>
                <p>Pterodactyl Panel</p>
                <p className="price">Starting At €{Number(packageDetail.price).toFixed(2).replace(/\.0+$/, "")}</p>
                <a
                  className="primary-link"
                  href={`${packageDetail.link}?options[${locationOption}]=${activeLocation}${affiliateID ? `&ref=${encodeURIComponent(affiliateID)}` : ""}`}
                >
                  Order Now
                </a>
              </div>
            ))}
            <div className="packagebox custom-package">
              <h3>Need something custom?</h3>
              <p>Tell us what you need and we’ll build a plan that fits your server perfectly.</p>
              <p>Custom RAM, CPU, or storage options are available.</p>
              <a className="primary-link" href="/contact">
                Contact us
              </a>
            </div>
          </div>
        </div>
        <div className="section-heading">
          <h2 className="section-title">Feature highlights</h2>
          <p className="section-subtitle">Everything included to power modded, plugin-ready communities.</p>
        </div>
        <div className="feature-grid">
          <div className="feature-card">
            <h3>Free migration help</h3>
            <p>Need help migrating from another host? We'll migrate you free of charge. Just contact us and we'll take it from there.</p>
            <button type="button" className="feature-link" onClick={openMigrationModal}>
              Click for more information
            </button>
          </div>
          <div className="feature-card">
            <h3>Mod support</h3>
            <p>Use the built-in mod installer to install popular mods with ease.</p>
          </div>
          <div className="feature-card">
            <h3>Modpack ready</h3>
            <p>Spin up curated modpacks fast with our modpack installer.</p>
          </div>
          <div className="feature-card">
            <h3>Plugin friendly</h3>
            <p>Add plugins in minutes with the integrated plugin installer.</p>
          </div>
          <div className="feature-card">
            <h3>Free custom subdomain</h3>
            <p>Get a branded subdomain so your community can connect instantly.</p>
          </div>
          <div className="feature-card">
            <h3>Need server splitting?</h3>
            <p>In need of a server split to make a BungeeCord server or split into multiple servers? Feel free to contact us and we can help you.</p>
            <button type="button" className="feature-link" onClick={openSplitModal}>
              Click for more information
            </button>
          </div>
        </div>
      </section>
      {showSplitModal && (
        <div className="split-modal-backdrop" onClick={closeSplitModal}>
          <div className="split-modal" onClick={(event) => event.stopPropagation()}>
            <div className="split-modal-header">
              <h3>Server splitting details</h3>
              <button type="button" className="split-modal-close" onClick={closeSplitModal} aria-label="Close">
                ×
              </button>
            </div>
            <p>
              Server splitting lets you carve one host into multiple smaller servers so you can use every GB of hardware efficiently. It’s perfect for BungeeCord networks or multi-mode setups without paying for extra machines. After we grant access, you can freely change the split whenever you want.
            </p>
            <div className="split-examples">
              <div className="split-example">
                <div className="split-example-header">
                  <span className="split-pill">Example split</span>
                  <span className="split-total">8GB total</span>
                </div>
                <div className="split-example-grid">
                  <div className="split-example-card">
                    <span className="split-label">BungeeCord</span>
                    <strong>1GB</strong>
                  </div>
                  <div className="split-example-card">
                    <span className="split-label">Hub</span>
                    <strong>2GB</strong>
                  </div>
                  <div className="split-example-card">
                    <span className="split-label">Gamemode</span>
                    <strong>5GB</strong>
                  </div>
                </div>
              </div>
              <div className="split-example">
                <div className="split-example-header">
                  <span className="split-pill">Example split</span>
                  <span className="split-total">12GB total</span>
                </div>
                <div className="split-example-grid">
                  <div className="split-example-card">
                    <span className="split-label">BungeeCord</span>
                    <strong>1GB</strong>
                  </div>
                  <div className="split-example-card">
                    <span className="split-label">Hub</span>
                    <strong>2GB</strong>
                  </div>
                  <div className="split-example-card">
                    <span className="split-label">Skyblock</span>
                    <strong>4GB</strong>
                  </div>
                  <div className="split-example-card">
                    <span className="split-label">Survival SMP</span>
                    <strong>5GB</strong>
                  </div>
                </div>
              </div>
              <p className="split-example-note">
                Need a custom split? Contact us and we’ll help map the perfect layout.
              </p>
            </div>
            <a className="split-modal-link" href="/contact">
              Contact us to plan a split
            </a>
            <button type="button" className="split-modal-cta" onClick={closeSplitModal}>
              Got it
            </button>
            <div className="split-scroll-hint">Scroll for more</div>
          </div>
        </div>
      )}
      {showMigrationModal && (
        <div className="split-modal-backdrop" onClick={closeMigrationModal}>
          <div className="split-modal" onClick={(event) => event.stopPropagation()}>
            <div className="split-modal-header">
              <h3>Free migration details</h3>
              <button type="button" className="split-modal-close" onClick={closeMigrationModal} aria-label="Close">
                ×
              </button>
            </div>
            <p>
              We can help transfer your server from another host using SFTP access from that host. Reach out and we’ll guide you through the steps.
            </p>
            <a className="split-modal-link" href="/contact">
              Contact us to request a migration
            </a>
            <button type="button" className="split-modal-cta" onClick={closeMigrationModal}>
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
